import { supabase } from './supabase'
import { logActivity, ActivityTypes } from '../utils/activityLogger'
import { normalizeContactFields, contactHasName } from './leadHelpers'
import {
  extractInternalConversionFields,
  stripInternalConversionCustomFields,
  resolveValidatedAssignedTo,
} from './leadConversionForm'

function buildCustomerContactsFromLead(lead: any) {
  if (lead.contacts && Array.isArray(lead.contacts) && lead.contacts.length > 0) {
    const contacts = lead.contacts
      .map((c: any, idx: number) => {
        const names = normalizeContactFields(c)
        return {
          id: c.id || `contact-${idx}`,
          first_name: names.first_name,
          last_name: names.last_name,
          name: names.name,
          title: c.title || undefined,
          phone: c.phone || '',
          cell: c.cell || '',
          email: c.email || '',
          extension: c.extension || undefined,
        }
      })
      .filter((c: any) => contactHasName(c) || c.email || c.phone || c.cell)

    if (contacts.length > 0) return contacts
  }

  const rootNames = normalizeContactFields({
    first_name: lead.contact_first_name,
    last_name: lead.contact_last_name,
  })

  if (rootNames.name || lead.email || lead.phone || lead.company_email || lead.company_phone) {
    return [
      {
        id: 'contact-0',
        first_name: rootNames.first_name,
        last_name: rootNames.last_name,
        name: rootNames.name,
        phone: lead.phone || lead.company_phone || '',
        cell: '',
        email: lead.email || lead.company_email || '',
      },
    ]
  }

  return null
}

async function migrateLeadActivityLogsToCustomer(leadId: string, customerId: string) {
  const { data: leadLogs, error } = await supabase
    .from('activity_logs')
    .select('*')
    .eq('entity_type', 'lead')
    .eq('entity_id', leadId)

  if (error) {
    console.error('Error fetching lead activity logs for migration:', error)
    return
  }

  if (!leadLogs?.length) return 0

  const activityLogs = leadLogs.map((activity) => ({
    activity_type: activity.activity_type,
    entity_type: 'customer',
    entity_id: customerId,
    description: activity.description || '',
    metadata: {
      ...(activity.metadata && typeof activity.metadata === 'object' ? activity.metadata : {}),
      migrated_from_lead: true,
      original_activity_log_id: activity.id,
      original_lead_id: leadId,
    },
    created_by: activity.created_by,
    user_id: activity.user_id || activity.created_by,
    created_at: activity.created_at,
  }))

  const { error: insertError } = await supabase.from('activity_logs').insert(activityLogs)
  if (insertError) {
    console.error('Error migrating lead activity logs:', insertError)
    return 0
  }

  console.log(`Migrated ${leadLogs.length} lead activity log(s) to customer`)
  return leadLogs.length
}

/**
 * Convert a lead to a customer when a contract is created or proposal is accepted
 */
export async function convertLeadToCustomer(leadId: string) {
  try {
    console.log('Starting lead conversion for leadId:', leadId)

    // 1. Fetch the lead
    const { data: lead, error: leadError } = await supabase
      .from('leads')
      .select('*')
      .eq('id', leadId)
      .single()

    if (leadError || !lead) {
      console.error('Lead fetch error:', leadError)
      throw new Error('Lead not found')
    }

    console.log('Lead fetched:', {
      id: lead.id,
      company_name: lead.company_name,
      projects_count: lead.projects?.length || 0,
      projects: lead.projects,
    })

    // 2. Check if customer already exists
    const { data: existingCustomer } = await supabase
      .from('customers')
      .select('id')
      .eq('company_name', lead.company_name || '')
      .single()

    if (existingCustomer) {
      // Customer already exists, migrate data and delete lead
      const conversionTimestamp = new Date().toISOString()

      const leadContacts = buildCustomerContactsFromLead(lead)
      if (leadContacts?.length) {
        const { data: existingFull } = await supabase
          .from('customers')
          .select('contacts')
          .eq('id', existingCustomer.id)
          .single()

        const existingContacts =
          existingFull?.contacts && Array.isArray(existingFull.contacts)
            ? existingFull.contacts
            : []
        const mergedContacts = [...existingContacts]
        for (const leadContact of leadContacts) {
          const alreadyPresent = mergedContacts.some(
            (existing: any) =>
              (leadContact.email && existing.email === leadContact.email) ||
              (leadContact.name &&
                existing.name === leadContact.name &&
                existing.phone === leadContact.phone)
          )
          if (!alreadyPresent) {
            mergedContacts.push(leadContact)
          }
        }

        await supabase
          .from('customers')
          .update({ contacts: mergedContacts })
          .eq('id', existingCustomer.id)
      }

      // Migrate lead activity history to customer activity_logs
      await migrateLeadActivityLogsToCustomer(leadId, existingCustomer.id)

      // Update proposal requests to link to customer
      await supabase
        .from('proposals')
        .update({ customer_id: existingCustomer.id, lead_id: null })
        .eq('lead_id', leadId)
        .eq('record_type', 'request')

      // Log conversion activity
      await logActivity({
        activity_type: ActivityTypes.LEAD_CONVERTED,
        entity_type: 'customer',
        entity_id: existingCustomer.id,
        description: `Lead "${lead.company_name || lead.contact_first_name}" converted to existing customer`,
        metadata: { lead_id: leadId, converted_at: conversionTimestamp },
      })

      // Delete the lead
      await supabase.from('leads').delete().eq('id', leadId)

      return existingCustomer.id
    }

    // 3. Create customer from lead data
    const primaryContact = lead.contacts && lead.contacts[0] ? lead.contacts[0] : null

    // Handle backwards compatibility: if contact has 'name' but not first_name/last_name, split it
    let contactFirstName = ''
    let contactLastName = ''
    if (primaryContact) {
      if (primaryContact.first_name || primaryContact.last_name) {
        contactFirstName = primaryContact.first_name || ''
        contactLastName = primaryContact.last_name || ''
      } else if (primaryContact.name) {
        const nameParts = primaryContact.name.trim().split(' ')
        contactFirstName = nameParts[0] || ''
        contactLastName = nameParts.slice(1).join(' ') || ''
      }
    }

    const conversionTimestamp = new Date().toISOString()

    const customerContacts = buildCustomerContactsFromLead(lead)

    const leadCustomFields =
      lead.custom_fields && typeof lead.custom_fields === 'object' ? { ...lead.custom_fields } : {}
    const { status: conversionStatus, customer_notes: conversionCustomerNotes, payment_method: conversionPaymentMethod, sales_tax_status: conversionSalesTaxStatus } =
      extractInternalConversionFields(leadCustomFields)
    const cleanedCustomFields = (() => {
      const cleaned = stripInternalConversionCustomFields(leadCustomFields)
      return Object.keys(cleaned).length > 0 ? cleaned : null
    })()

    const customerData: any = {
      company_name: lead.company_name || null,
      contact_first_name: contactFirstName || lead.contact_first_name || '',
      contact_last_name: contactLastName || lead.contact_last_name || '',
      email: lead.company_email || primaryContact?.email || lead.email || null,
      phone: lead.company_phone || primaryContact?.phone || lead.phone || null,
      website: lead.company_website || null,
      address: lead.company_address || lead.address || null,
      address_line_2: lead.company_address_line_2 || null,
      city: lead.company_city || lead.city || null,
      state: lead.company_state || lead.state || null,
      zip_code: lead.company_zip || lead.zip_code || null,
      contacts: customerContacts,
      status: conversionStatus,
      is_active: true,
      assigned_to: await resolveValidatedAssignedTo(lead.assigned_to),
      payment_method: conversionPaymentMethod || null,
      sales_tax_status: conversionSalesTaxStatus || null,
      custom_fields: cleanedCustomFields,
      converted_from_lead_id: leadId,
      converted_at: conversionTimestamp,
    }

    const { data: customer, error: customerError } = await supabase
      .from('customers')
      .insert([customerData])
      .select()
      .single()

    if (customerError) throw customerError

    const customerLabel = lead.company_name || contactFirstName || 'Customer'
    await logActivity({
      activity_type: ActivityTypes.CUSTOMER_CREATED,
      entity_type: 'customer',
      entity_id: customer.id,
      description: `Customer "${customerLabel}" created from lead conversion`,
      metadata: { lead_id: leadId, converted_at: conversionTimestamp },
    })

    // 3.5. Migrate notes from lead_notes table to customer notes
    const { data: leadNotesData, error: notesError } = await supabase
      .from('lead_notes')
      .select('note, created_at, created_by_name, note_type')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: true })

    const migratedLeadNotes =
      !notesError && leadNotesData?.length
        ? leadNotesData.map((note) => ({
            note: note.note,
            timestamp: note.created_at,
            created_by: note.created_by_name || null,
            note_type: note.note_type || 'general',
          }))
        : []

    const allCustomerNotes = [...conversionCustomerNotes, ...migratedLeadNotes]
    if (allCustomerNotes.length > 0) {
      await supabase.from('customers').update({ notes: allCustomerNotes }).eq('id', customer.id)
    }

    // 3.6. Migrate lead activity history to customer activity_logs
    await migrateLeadActivityLogsToCustomer(leadId, customer.id)

    // 4. Create properties from lead projects and track address to property ID mapping
    const projectAddressToPropertyId: { [address: string]: string } = {}
    console.log('Creating properties from lead projects. Project count:', lead.projects?.length || 0)

    if (lead.projects && lead.projects.length > 0) {
      for (const project of lead.projects) {
        console.log('Processing project:', project)

        if (project.address || project.name) {
          const propertyName =
            (typeof project.name === 'string' && project.name.trim()) ||
            project.address ||
            'Property'

          const additionalAccessData = (Array.isArray(project.additional_access)
            ? project.additional_access
            : []
          )
            .filter((a: any) => a.location && a.access_type && a.access_info)
            .map((a: any) => ({
              location: a.location,
              access_type: a.access_type,
              access_info: a.access_info,
            }))

          let propertyContacts: any[] = []
          if (
            Array.isArray(project.selected_contact_ids) &&
            project.selected_contact_ids.length > 0 &&
            customerContacts
          ) {
            propertyContacts = customerContacts.filter((c: any) =>
              project.selected_contact_ids.includes(c.id)
            )
          }

          const propertyNotes = Array.isArray(project.property_notes)
            ? project.property_notes
            : project.notes
              ? [{ timestamp: new Date().toISOString(), note: project.notes }]
              : null

          const insertData: any = {
            customer_id: customer.id,
            name: propertyName,
            address: project.address || null,
            city: project.city || null,
            state: project.state || null,
            zip_code: project.zip || null,
            address_line_2: project.address_line_2 || null,
            property_type: project.type || project.building_type || null,
            building_type: project.building_type || project.type || null,
            units: project.units || project.unit_count || null,
            stories: project.stories || null,
            access_type: project.access_type || null,
            access_info: project.access_info || null,
            additional_access: additionalAccessData.length > 0 ? additionalAccessData : null,
            payment_method: project.payment_method || null,
            sales_tax_status: project.sales_tax_status || null,
            contacts: propertyContacts,
            unit_count: project.unit_count ? parseInt(String(project.unit_count), 10) : null,
          }

          const { data: property, error: propError } = await supabase
            .from('properties')
            .insert(insertData)
            .select('id')
            .single()

          if (propError) {
            console.error('Error creating property:', propError)
            throw propError
          }

          if (property && propertyNotes?.length) {
            await supabase
              .from('properties')
              .update({ notes: propertyNotes })
              .eq('id', property.id)
          }

          if (property && project.address) {
            // Map project address to property ID for proposal updates
            // Use the street address for matching (since proposals store project_address as just the street address)
            projectAddressToPropertyId[project.address] = property.id
            console.log(`Created property ${property.id} for project address: ${project.address}`)
          }

          if (property) {
            await logActivity({
              activity_type: ActivityTypes.PROPERTY_CREATED,
              entity_type: 'customer',
              entity_id: customer.id,
              description: `Property "${propertyName}" created during lead conversion`,
              metadata: { property_id: property.id, lead_id: leadId },
            })
          }
        }
      }
    }

    // 5. Update all proposals linked to this lead
    // Find proposals with this lead_id
    const { data: proposals, error: proposalsError } = await supabase
      .from('proposals')
      .select('id, project_address, property_id')
      .eq('lead_id', leadId)

    if (!proposalsError && proposals && proposals.length > 0) {
      for (const proposal of proposals) {
        const updateData: any = {
          customer_id: customer.id,
          lead_id: null, // Remove lead_id since it's now a customer
        }

        // Try multiple methods to match project to property:

        // Method 1: If proposal has project_address column and value, find matching property
        if (proposal.project_address && projectAddressToPropertyId[proposal.project_address]) {
          updateData.property_id = projectAddressToPropertyId[proposal.project_address]
        }

        // Method 2: If property_id contains project-${index}, try to match by project index
        if (
          !updateData.property_id &&
          typeof proposal.property_id === 'string' &&
          proposal.property_id.includes('project-')
        ) {
          const projectIndex = parseInt(proposal.property_id.replace('project-', ''))
          if (projectIndex >= 0 && lead.projects && lead.projects[projectIndex]?.address) {
            const projectAddress = lead.projects[projectIndex].address
            if (projectAddressToPropertyId[projectAddress]) {
              updateData.property_id = projectAddressToPropertyId[projectAddress]
            }
          }
        }

        // Method 3: If property_id looks like a project ID (but not a UUID), try to find by matching project
        if (
          !updateData.property_id &&
          proposal.property_id &&
          typeof proposal.property_id === 'string' &&
          !proposal.property_id.match(
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
          )
        ) {
          // Not a UUID, might be a project ID or index
          const matchingProject = lead.projects?.find(
            (p: any, idx: number) =>
              p.id === proposal.property_id || proposal.property_id === `project-${idx}`
          )
          if (matchingProject?.address && projectAddressToPropertyId[matchingProject.address]) {
            updateData.property_id = projectAddressToPropertyId[matchingProject.address]
          }
        }

        // Method 4: Fallback - if no property matched but we have projects, use the first property created
        if (!updateData.property_id && lead.projects && lead.projects.length > 0 && Object.keys(projectAddressToPropertyId).length > 0) {
          // Use the first property that was created
          const firstPropertyId = Object.values(projectAddressToPropertyId)[0]
          updateData.property_id = firstPropertyId as string
          console.log(`Using fallback: linking proposal to first property ${firstPropertyId}`)
        }

        // Only update if we have something to change
        if (updateData.property_id || updateData.customer_id) {
          const { error: updateError } = await supabase.from('proposals').update(updateData).eq('id', proposal.id)

          if (updateError) {
            console.error(`Error updating proposal ${proposal.id}:`, updateError)
            console.error('Update data:', updateData)
          } else {
            console.log(`Updated proposal ${proposal.id} with customer_id: ${customer.id}, property_id: ${updateData.property_id || 'none'}`)
          }
        } else {
          console.warn(`No property matched for proposal ${proposal.id}. Created ${Object.keys(projectAddressToPropertyId).length} properties.`)
        }
      }
    }

    // 6. Update proposal requests to link to customer
    const { error: proposalRequestsError } = await supabase
      .from('proposals')
      .update({ customer_id: customer.id, lead_id: null })
      .eq('lead_id', leadId)
      .eq('record_type', 'request')

    if (proposalRequestsError) {
      console.error('Error updating proposal requests:', proposalRequestsError)
      // Don't throw - continue with conversion even if proposal request update fails
    } else {
      console.log('Updated proposal requests to link to customer')
    }

    // 7. Log conversion activity
    await logActivity({
      activity_type: ActivityTypes.LEAD_CONVERTED,
      entity_type: 'customer',
      entity_id: customer.id,
      description: `Lead "${lead.company_name || lead.contact_first_name}" converted to customer`,
      metadata: { lead_id: leadId, converted_at: conversionTimestamp },
    })

    // 8. Delete the lead after all migrations are complete
    const { error: deleteError } = await supabase.from('leads').delete().eq('id', leadId)

    if (deleteError) {
      console.error('Error deleting lead:', deleteError)
      throw new Error(`Failed to delete lead after conversion: ${deleteError.message}`)
    } else {
      console.log(`Successfully deleted lead ${leadId} after conversion to customer ${customer.id}`)
    }

    return customer.id
  } catch (error) {
    console.error('Error converting lead to customer:', error)
    throw error
  }
}

/**
 * Convert a lead to a customer and link it to a proposal
 */
export async function convertLeadForProposal(leadId: string, proposalId: string) {
  const customerId = await convertLeadToCustomer(leadId)

  // Update the proposal with the customer ID
  await supabase.from('proposals').update({ customer_id: customerId }).eq('id', proposalId)

  return customerId
}
